import * as React from "react";
import {
  Grid,
  Title,
  Text,
  Divider,
  Badge,
  useMantineTheme,
  ActionIcon,
  Flex,
  Modal,
  Stack,
  Tooltip,
  Select,
} from "@mantine/core";
import { useSearchParams, useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import {
  IconX,
  IconCheck,
  IconPin,
  IconCirclePlus,
  IconEdit,
  IconPinned,
  IconLogs,
  IconLayout,
} from "@tabler/icons-react";
import { showNotification } from "@mantine/notifications";
import Loading from "../ui/Loading";
import { useUseCaseBasic, useUseCaseRun, useUseCaseRunIdAccess, useUseCaseRunIds } from "../hooks";
import { getUseCaseRunIds } from "../api";
import NewRun from "./View/NewRun";
import { UseCaseBasicType, UseCaseRunDetails } from "@supportinitiative/common";
import { UseCaseGrid } from "./View/UseCaseGrid";
import { UserContext } from "src/misc/UserContext";
import { getColor } from "./UseCaseUtils";

// TODO: Custom Actions

function isPinned(entityId: string | null | undefined, id: string | null | undefined, userPinnedDetails: any) {
  let pinned = (userPinnedDetails || []).some(
    (pinnedDetail: any) =>
      pinnedDetail.pin.entityId === entityId && pinnedDetail.pin.type === "RECONSET" && pinnedDetail.pin.pinId === id,
  );
  return pinned;
}

function Details({ details }: { details: UseCaseRunDetails["details"] }) {
  const theme = useMantineTheme();

  return (
    <Grid gutter={"xs"} columns={12}>
      <Grid.Col span={12}>
        <Flex gap="xs" align={"center"}>
          <Stack gap="0">
            <Text size="xs" fw={500} mb="xxs" inline>
              Run Seq
            </Text>
            <Text size="xs" inline c="dimmed">
              {details.runSeq}
            </Text>
          </Stack>

          <Divider orientation="vertical" />
          <Stack gap="0">
            <Text size="xs" fw={500} mb="xxs" inline>
              Description
            </Text>
            <Text size="xs" inline c="dimmed">
              {details.description}
            </Text>
          </Stack>

          <Divider orientation="vertical" />
          <Stack gap="0">
            <Text size="xs" fw={500} mb="xxs" inline>
              Start Date
            </Text>
            <Text size="xs" inline c="dimmed">
              {dayjs(details.startDate).format("YYYY-MM-DD HH:mm:ss")}
            </Text>
          </Stack>
          {details.status === "COMPLETE" && (
            <>
              <Divider orientation="vertical" />
              <Stack gap="0">
                <Text size="xs" fw={500} mb="xxs" inline>
                  End Date
                </Text>
                <Text size="xs" inline c="dimmed">
                  {dayjs(details.endDate).format("YYYY-MM-DD HH:mm:ss")}
                </Text>
              </Stack>
            </>
          )}
          <Divider orientation="vertical" />

          <Stack gap="0">
            <Text size="xs" fw={500} mb="xxs" inline>
              Status
            </Text>
            <Badge
              radius="xs"
              color={getColor(details.status)}
              // variant="light"
              style={{ marginRight: theme.spacing.xs }}
            >
              {details.status}
            </Badge>
          </Stack>
        </Flex>
      </Grid.Col>
    </Grid>
  );
}

function UserActions({
  useCase,
  runIds,
  runIdAccess,
  setRunId,
  setRunIdAccess,
  reOrg,
  setReOrg,
}: {
  useCase: UseCaseBasicType | undefined;
  runIds: Array<{ label: string; value: string }>;
  runIdAccess: Array<{ label: string; value: string }>;
  setRunId: (runId: string | null | undefined) => void;
  setRunIdAccess: (access: string | null | undefined) => void;
  reOrg: boolean;
  setReOrg: (value: boolean) => void;
}) {
  let [searchParams] = useSearchParams();
  let id = searchParams.get("id"),
    entityId = searchParams.get("entityId");
  const navigate = useNavigate();
  const theme = useMantineTheme();

  const { state: userState, dispatch: userStateDispatch } = React.useContext(UserContext);

  // New run
  const [showNewRun, setShowNewRun] = React.useState(false);

  // Logs
  const [showLogs, setShowLogs] = React.useState(false);

  if (useCase) {
    return (
      <>
        <Flex justify={"flex-start"} gap={"xs"}>
          <Tooltip label="New">
            <ActionIcon
              variant="light"
              size="lg"
              onClick={() => {
                setShowNewRun(true);
              }}
            >
              <IconCirclePlus style={{ width: "70%", height: "70%" }} stroke={2} />
            </ActionIcon>
          </Tooltip>

          <Tooltip label="Logs">
            <ActionIcon variant="light" size="lg">
              <IconLogs
                style={{ width: "70%", height: "70%" }}
                stroke={2}
                onClick={() => {
                  setShowLogs(true);
                }}
              />
            </ActionIcon>
          </Tooltip>

          <Tooltip label="Edit">
            <ActionIcon variant="light" size="lg">
              <IconEdit
                style={{ width: "70%", height: "70%" }}
                stroke={2}
                onClick={() => {
                  navigate(`/app/usecases/edit?id=${id}&entityId=${entityId}`);
                }}
              />
            </ActionIcon>
          </Tooltip>

          <Tooltip label="Edit">
            <ActionIcon variant={reOrg ? "filled" : "light"} size="lg">
              <IconLayout
                style={{ width: "70%", height: "70%" }}
                stroke={2}
                onClick={() => {
                  setReOrg(!reOrg);
                }}
              />
            </ActionIcon>
          </Tooltip>

          <Select
            placeholder={"Run ID"}
            data={runIds}
            searchable
            clearable
            value={userState.useCase.runId}
            onChange={(value) => {
              setRunId(value);
            }}
          />
          <Select
            placeholder={"Access"}
            data={runIdAccess}
            searchable
            clearable
            value={userState.useCase.access}
            onChange={(value) => {
              if (value) {
                setRunIdAccess(value);
              } else {
                setRunIdAccess("*");
              }
            }}
          />
        </Flex>

        <Modal
          centered
          size={"xl"}
          opened={showNewRun}
          onClose={() => setShowNewRun(false)}
          title={<Title order={4}>New Run</Title>}
        >
          <NewRun
            entityId={entityId}
            id={id}
            setRunId={(runId: string) => {
              setRunId(runId);
            }}
            inputs={useCase.definition.inputs}
            cancel={() => setShowNewRun(false)}
          />
        </Modal>
      </>
    );
  } else {
    return <></>;
  }
}

export default function UseCaseView() {
  let [searchParams] = useSearchParams();
  let id = searchParams.get("id"),
    entityId = searchParams.get("entityId");
  const navigate = useNavigate();
  const theme = useMantineTheme();

  const { state: userState, dispatch: userStateDispatch } = React.useContext(UserContext);
  // Assuem has some runs
  const [hasRun, setHasRun] = React.useState(true);

  // Reorg grid
  const [reOrg, setReOrg] = React.useState(false);

  const setRunId = (value: string | null | undefined) => {
    if (value) {
      setHasRun(true);
    }
    userStateDispatch({
      type: "SET-USECASE-RUN-ID",
      value: value,
    });
  };

  const setRunIdAccess = (value: string | null | undefined) => {
    userStateDispatch({
      type: "SET-USECASE-RUN-ID-ACCESS",
      value: value,
    });
  };

  const { data: useCase } = useUseCaseBasic(entityId, id);

  // List of runs and access
  const { data: runIds } = useUseCaseRunIds(entityId, id);

  const { data: runIdAccess } = useUseCaseRunIdAccess(entityId, id, userState.useCase.runId);

  // Run
  const { data: run } = useUseCaseRun(entityId, id, [
    {
      name: "__runId",
      value: userState.useCase.runId,
    },
    {
      name: "__access",
      value: userState.useCase.access,
    },
  ]);

  React.useEffect(() => {
    const getRunId = async () => {
      let runIds = await getUseCaseRunIds(entityId, id, "__runid");

      if (runIds && runIds.length > 0) {
        setHasRun(true);
        if (userState.useCase.runId) {
          // console.log("dont do anything =>", userState.useCase.runId);
        } else {
          let newRunId = runIds[0].value;
          if (newRunId !== userState.useCase.runId) {
            // If the runId is different, reset access to "*"
            // Ideally we need to check the previous access and set it accordingly if the same access key applicable for current run as well
            // But for now, we will reset it to "*"
            // console.log("setting run id access");
            setRunIdAccess("*");
          }
          setRunId(newRunId);
        }
      } else {
        setHasRun(false);
        setRunId("");
        setRunIdAccess("*");
      }
    };

    getRunId();

    // getLOVData("_runid", []);
  }, [entityId, id]);

  // console.log("hasRun", hasRun);
  return (
    <Stack gap="0">
      <div id="header">
        <Grid
          align="flex-end"
          gutter={"xs"}
          mt="xs"
          mb="xs"
          // mb="xs"
          style={{
            display: "flex",
            // borderLeft: `${theme.spacing.xxs} solid ${theme.colors.emerald[7]}`,
          }}
          columns={12}
        >
          <Grid.Col span={2} mr="xs" style={{ borderRight: `2px solid ${theme.colors.dark[3]}` }}>
            <Flex align={"end"} gap={theme.spacing.xs}>
              <Stack gap={0}>
                <Title order={3} c="dark">
                  {useCase ? useCase.name : "Use Case"}
                </Title>
                {useCase && (
                  <Text size="sm" c="dark">
                    {useCase.displayName}
                  </Text>
                )}
              </Stack>
            </Flex>
          </Grid.Col>
          <Grid.Col span={9}>
            <Flex align={"flex-start"} gap="xs">
              <UserActions
                useCase={useCase}
                setRunId={setRunId}
                setRunIdAccess={setRunIdAccess}
                runIds={runIds ? runIds : []}
                runIdAccess={runIdAccess ? runIdAccess : []}
                reOrg={reOrg}
                setReOrg={setReOrg}
              />
              {run && <Details details={run?.details} />}
            </Flex>
          </Grid.Col>
        </Grid>
      </div>

      {/* <Divider size="lg" mb="xs" color="dark.2" /> */}
      {/* <QueryColumns useCase={useCase} /> */}

      <div id="body">
        {!hasRun ? (
          <>
            <Divider size="sm" mb="xs" color="dark.2" />
            <Divider mt="lg" label="No runs available" labelPosition="left" />
          </>
        ) : (
          <>
            {run && useCase ? (
              <>
                <Divider size="sm" mb="xs" color="dark.2" />
                <UseCaseGrid useCase={useCase} run={run} access={userState.useCase.access} reOrg={reOrg} />
              </>
            ) : (
              <Grid align="center" gutter={"xs"} mb="xs" mt="lg">
                <Grid.Col span={4}>
                  <Loading />
                </Grid.Col>
              </Grid>
            )}
          </>
        )}
      </div>
    </Stack>
  );
}
